import { useState } from "react";

import { violationOf, violationsOf, type ClientErrors, type FormRules, type ValueOf } from "./validation.js";

/** 送る前の検証の状態と、画面に渡すもの。 */
export interface FieldCheck {
    /** その欄の、送る前の断り。無ければ undefined。 */
    readonly error: (name: string) => string | undefined;
    /**
     * 送る前に全部の欄を見る。<b>断った欄があれば、最初の欄の名前を返す</b>（送らない）。
     * 無ければ undefined（送ってよい）。
     */
    readonly checkAll: (valueOf: ValueOf) => string | undefined;
    /** 入力のたびに、<b>断りを出している欄だけ</b>を見直す（直せばその場で消える）。 */
    readonly recheck: (valueOf: ValueOf) => void;
    /** 欄を離れたとき。<b>値があるときだけ</b>見る（空の欄を素通りしただけでは断らない）。 */
    readonly leave: (name: string, valueOf: ValueOf) => void;
}

/**
 * 送る前の検証。
 *
 * <b>送るまでは黙っている</b>——打ち始めから断ると煩わしい。送ろうとして断った欄は、
 * 入力のたびに見直す。断りを出していない欄は、離れたときに値があれば見る。
 */
export const useFieldCheck = (rules: FormRules): FieldCheck => {
    const [errors, setErrors] = useState<ClientErrors>({});

    const put = (name: string, valueOf: ValueOf): void => {
        const message = violationOf(name, rules, valueOf);
        setErrors((current) => {
            if (current[name] === message) {
                return current;
            }
            const next: Record<string, string> = { ...current };
            if (message === undefined) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- 欄の名前ごとの断りを外す
                delete next[name];
            } else {
                next[name] = message;
            }
            return next;
        });
    };

    return {
        error: (name) => errors[name],
        checkAll: (valueOf) => {
            const found = violationsOf(rules, valueOf);
            setErrors(found);
            return Object.keys(found)[0];
        },
        recheck: (valueOf) => {
            for (const name of Object.keys(errors)) {
                put(name, valueOf);
            }
        },
        leave: (name, valueOf) => {
            if (rules[name] !== undefined && valueOf(name) !== "") {
                put(name, valueOf);
            }
        },
    };
};

/**
 * フォームの欄の値を読む（値を DOM が持つフォーム）。
 * チェックボックスは "true" / "false"、ラジオは選んだものの値（無ければ空）。
 */
export const formValueOf = (form: HTMLFormElement): ValueOf => (name) => {
    const found = form.elements.namedItem(name);
    if (found instanceof HTMLInputElement && found.type === "checkbox") {
        return found.checked ? "true" : "false";
    }
    // 同じ名前の欄が複数なら RadioNodeList（value は選んだラジオの値）
    return found !== null && "value" in found ? found.value : "";
};

/** 最初に断った欄へ移る（読み上げでも断りに気付けるように）。 */
export const focusField = (root: ParentNode, name: string | undefined): void => {
    if (name !== undefined) {
        root.querySelector<HTMLElement>(`[name="${CSS.escape(name)}"]`)?.focus();
    }
};
